import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import PublicLayout from "@/components/PublicLayout"
import { HeroBand, type HeroPhoto } from "@/components/gisela/HeroBand"
import { ContactBand, PhotoEnquiryBand } from "@/components/gisela/ClosingBands"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { orderBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps, PlacementPhoto } from "@/types/inertia"

interface Section {
  heading?: string
  body?: string
}

interface Props {
  content: {
    hero: { heading?: string; intro?: string; photo?: HeroPhoto | null; _hidden?: boolean }
    fees: Section & { table?: string }
    sessions: Section
    how_it_works: Section
    payment: Section
    insurance: Section
    concessions: Section
    policies: Section
    crisis: Section
    governance: Section
    faq: { body?: string }
    // The closing pair every page of hers ends on.
    cta?: { photo?: HeroPhoto | null; _hidden?: boolean }
    contact?: { heading?: string; body?: string; _hidden?: boolean }
  }
  /** The home page's tile slot — one card per page of her site but this one. */
  tile_photos?: Record<string, PlacementPhoto | null>
  /** A room of hers, standing beside the short enquiry form. */
  contact_photo?: PlacementPhoto | null
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

export default function Fees({ content, tile_photos = {}, contact_photo, section_order, meta }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.fees
  const tc = props.translations.gisela_pages.common
  const title = meta?.title ?? "Fees"
  const description =
    meta?.description ??
    "Therapy with Gisela Ramirez is £100 for a 50-minute session, online or in person, and starts with a free 15-minute consultation. Payment, insurance, concessions and cancellation explained."

  // Every band is presence-gated, so the cream/stone alternation can't be
  // hardcoded per section — blank one in the admin and two same-coloured bands
  // would end up adjacent. Describe them (each named by its registry entry
  // id), put them in the admin's order, drop the empty ones, then colour by
  // index. The crisis callout is deliberately not a band: it sits inside
  // whichever band colour comes next, as an interruption.
  const bands = orderBands(
    [
      { id: "therapy-fees", heading: content.fees.heading || tc.fees, body: content.fees.body ?? "", table: content.fees.table },
      { id: "gisela-booking-sessions", heading: content.sessions.heading || t.sessions_heading, body: content.sessions.body ?? "" },
      { id: "gisela-booking-how-it-works", heading: t.how_heading, body: content.how_it_works.body ?? "" },
      { id: "gisela-booking-payment", heading: content.payment.heading || t.payment_heading, body: content.payment.body ?? "" },
      { id: "gisela-booking-insurance", heading: content.insurance.heading || t.insurance_heading, body: content.insurance.body ?? "" },
      { id: "gisela-booking-concessions", heading: content.concessions.heading || t.concessions_heading, body: content.concessions.body ?? "" },
      { id: "gisela-booking-policies", heading: content.policies.heading || t.policies_heading, body: content.policies.body ?? "" },
      { id: "gisela-booking-crisis", heading: content.crisis.heading || t.crisis_heading, body: content.crisis.body ?? "" },
      { id: "gisela-booking-governance", heading: content.governance.heading || t.governance_heading, body: content.governance.body ?? "" },
      { id: "gisela-booking-faq", heading: tc.common_questions, body: content.faq.body ?? "" },
    ] as { id: string; heading: string; body: string; table?: string }[],
    section_order,
  ).filter((band) => band.body)

  const bg = (i: number) => (i % 2 === 0 ? "bg-cream" : "bg-stone")

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {/* Hero */}
        {!sectionHidden(content.hero) && (
          <HeroBand photo={content.hero.photo}>
            <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">
              {t.eyebrow}
            </p>
            <h1 className="text-white">{content.hero.heading || t.hero_heading}</h1>
            {content.hero.intro && <p className="mt-4 max-w-2xl text-lg text-white/80">{content.hero.intro}</p>}
          </HeroBand>
        )}

        {bands.map((band, i) =>
          band.id === "gisela-booking-crisis" ? (
            /* Urgent help — a narrower, ruled card rather than another band, so
               it reads as an interruption on either background colour. */
            <section key={band.id} className={`${bg(i)} py-16`}>
              <div className="mx-auto max-w-3xl px-6 lg:px-8">
                <div className="rounded-2xl border-l-4 border-teal bg-white p-8 ring-1 ring-hairline">
                  <h2>{band.heading}</h2>
                  <div className="body-content mt-4 text-ink-body">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{band.body}</ReactMarkdown>
                  </div>
                </div>
              </div>
            </section>
          ) : (
            <section key={band.id} className={`${bg(i)} py-16`}>
              <div className="mx-auto grid max-w-6xl grid-cols-1 px-6 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)] lg:gap-x-16 lg:px-8">
                <h2 className="lg:sticky lg:top-24 lg:self-start">{band.heading}</h2>
                <div className="min-w-0">
                  <div className="body-content mt-6 text-ink-body">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{band.body}</ReactMarkdown>
                  </div>
                  {/* The fee table is its own field, so the whole markdown output
                      is the table and the scroll container is exactly right. */}
                  {band.table && (
                    <div className="body-content mt-6 overflow-x-auto text-ink-body">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{band.table}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>
            </section>
          ),
        )}

        {/* The close every page of hers ends on — the invitation, the tiles
            out of it, then a room beside the short form. Someone who has read
            the prices is deciding, and this is both ways of saying yes. */}
        {!sectionHidden(content.cta) && (
          <ContactBand heading={t.cta_heading} body={t.cta_body} photo={content.cta?.photo} />
        )}

        {/* This page's own tile is left out — the reader is on it. */}
        <GiselaPageTiles photos={tile_photos} exclude={["/fees"]} />

        {!sectionHidden(content.contact) && (
          <PhotoEnquiryBand
            heading={content.contact?.heading}
            body={content.contact?.body}
            photo={contact_photo}
          />
        )}
      </PublicLayout>
    </>
  )
}
